import React, { useEffect, useState } from "react";
import { Link, Spin, Table, Tag, Typography } from "@arco-design/web-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import { FetchApi } from "../Api/AuthenticatedApi";
import { valueToPercent } from "../Broadcast/broadcastStats";
import { sequenceStatusMeta } from "../Sequences/sequenceMeta";
import { rangeLabel, viewerTimeZone } from "./reportRange";
import { sequenceReport } from "./sequenceReport";
import { plainClick } from "./plainClick";
import { reportLink } from "../utils/reportBack";

const { Text } = Typography;

// Its own section, loaded apart from signup activity: these numbers don't depend
// on the device filter, and a failure here leaves the rest of the report alone.
export default function SequenceSection({ range }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const [sequences, setSequences] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSequences(null);
    setFailed(false);
    const q = new URLSearchParams({ from: range[0], to: range[1], tz: viewerTimeZone() });
    FetchApi(getAccessTokenSilently, "/api/reports/sequences?" + q.toString(),
      (d) => setSequences((d && d.sequences) || []),
      () => navigate("/login"),
      () => setFailed(true));
  }, [range, getAccessTokenSilently, navigate]);

  const report = sequenceReport(sequences);
  const n = (x) => (x || 0).toLocaleString();
  // Links out carry a way back here, with these filters.
  const toReportLink = (path) => reportLink(path, location.pathname + location.search);
  const open = (path) => plainClick(navigate, toReportLink(path));

  let body;
  if (failed) {
    body = <Text type="secondary">Couldn't load sequence numbers.</Text>;
  } else if (sequences && report.sequences === 0) {
    body = (
      <Text type="secondary">
        You don't have any sequences yet. <Link href={toReportLink("/sequences")} onClick={open("/sequences")}>Go to Sequences</Link>
      </Text>
    );
  } else {
    body = (
      <Spin loading={!sequences} block>
        <SummaryCardGroup>
          <SummaryCard title="Sequences running" value={`${n(report.running)} of ${n(report.sequences)}`} />
          <SummaryCard title="People in progress" value={n(report.inProgress)} />
          <SummaryCard title="People finished" value={n(report.finished)} />
          <SummaryCard title="Emails sent" value={n(report.sent)} />
          <SummaryCard title="Opened" value={n(report.opened)} />
          <SummaryCard title="Open rate" value={valueToPercent(report.openRate)} />
          <SummaryCard title="Unsubscribed" value={n(report.unsubscribed)} />
        </SummaryCardGroup>
        <Table
          size="small"
          rowKey="id"
          style={{ marginTop: 12 }}
          pagination={report.rows.length > 10 ? { pageSize: 10, size: "small" } : false}
          data={report.rows}
          columns={[
            {
              title: "Sequence",
              dataIndex: "name",
              render: (name, row) => <Link href={toReportLink(`/sequences/${row.id}/edit`)} onClick={open(`/sequences/${row.id}/edit`)}>{name}</Link>,
            },
            {
              title: "Status",
              dataIndex: "status",
              render: (status) => {
                const meta = sequenceStatusMeta(status);
                return <Tag color={meta.color}>{meta.label}</Tag>;
              },
            },
            { title: "In progress", dataIndex: "active", render: n },
            { title: "Finished", dataIndex: "completed", render: n },
            { title: "Left early", dataIndex: "cancelled", render: n },
            { title: "Sent", dataIndex: "sent", render: n },
            { title: "Opened", dataIndex: "opened", render: n },
            { title: "Open rate", dataIndex: "openRate", render: valueToPercent },
            { title: "Unsubscribed", dataIndex: "unsubscribed", render: n },
          ]}
        />
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          Sent, opened and unsubscribed are for emails sent in these dates. Opening an email twice counts once.
          People in progress and finished are as of now, whatever the dates.
        </Text>
      </Spin>
    );
  }

  return <SummaryCard title="Sequences" subTitle={rangeLabel(range)}>{body}</SummaryCard>;
}
